/**
 * FloatingCallWidget — compact docked softphone panel for desktop web.
 *
 * Desktop counterpart to the mobile full-screen ActiveCallOverlay: a small bottom-right card driven by
 * the same `useVoiceCall()` state, with mute / DTMF keypad / audio-output picker / end-call, plus a
 * minimized pill. Portalled to <body> so it escapes in-app stacking contexts.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Hash,
  Volume2,
  Bluetooth,
  Headphones,
  Check,
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  Briefcase,
  ExternalLink,
} from 'lucide-react';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import type { AudioDeviceType, CallSummary } from '@/lib/twilioVoice';
import { DialPad } from './DialPad';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/** Label for one roster row — same fallbacks as the widget header. */
function callLabel(c: CallSummary): string {
  return c.displayName
    || (c.remoteNumber && !c.remoteNumber.startsWith('contact:') ? c.remoteNumber : 'Ongoing call');
}

/** Status text for one line in the multi-call list — each carries its own running duration. */
function lineStatus(c: CallSummary, now: number): string {
  switch (c.status) {
    case 'held': return 'On hold';
    case 'incoming': return 'Ringing…';
    case 'reconnecting': return 'Reconnecting…';
    case 'connected':
      return c.connectedAt ? formatDuration(Math.max(0, Math.floor((now - c.connectedAt) / 1000))) : 'Connected';
    default: return 'Connecting…';
  }
}

/**
 * Toggle-button backgrounds. The "on" state stays a colored fill rather than white: every control
 * inherits `text-white`, so a white fill would leave the icon and label invisible.
 */
const TOGGLE_ON = 'bg-blue-500 hover:bg-blue-600';
const TOGGLE_OFF = 'bg-white/10 hover:bg-white/20';

const OUTPUT_ICONS: Record<AudioDeviceType, React.ReactNode> = {
  earpiece: <Volume2 className="h-4 w-4" />,
  speaker: <Volume2 className="h-4 w-4" />,
  bluetooth: <Bluetooth className="h-4 w-4" />,
  wired: <Headphones className="h-4 w-4" />,
};

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Lets the widget be dragged around the viewport by a handle.
 *
 * Position is null until the first drag, so the default docked corner stays pure CSS. Coordinates are
 * always clamped inside the viewport — including on resize — because a widget dragged out of reach
 * during a live call would leave no way to hang up.
 */
function useDragPosition() {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  /** Grab offset within the element, so it doesn't jump to the cursor on grab. */
  const grab = useRef<{ dx: number; dy: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const clampToViewport = useCallback((x: number, y: number) => {
    const el = ref.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    return {
      x: clamp(x, 0, Math.max(0, window.innerWidth - w)),
      y: clamp(y, 0, Math.max(0, window.innerHeight - h)),
    };
  }, []);

  // Shrinking the window must not strand the widget outside it.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clampToViewport(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pos, clampToViewport]);

  const onPointerDown = (e: React.PointerEvent) => {
    // Never hijack a control (minimize, etc.) that happens to sit in the drag handle.
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    grab.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!grab.current) return;
    setPos(clampToViewport(e.clientX - grab.current.dx, e.clientY - grab.current.dy));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!grab.current) return;
    grab.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return {
    ref,
    /** Once dragged, position explicitly; until then the default docked classes apply. */
    style: pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}

export function FloatingCallWidget() {
  const {
    callState,
    remoteNumber,
    remoteName,
    isMuted,
    audioRoute,
    availableAudioDevices,
    callDuration,
    error,
    canRedial,
    calls,
    endCall,
    redial,
    toggleMute,
    selectAudioDevice,
    setHold,
    endCallById,
    openJob,
  } = useVoiceCall();

  const [minimized, setMinimized] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState('');
  const [showOutputs, setShowOutputs] = useState(false);
  const [selectedOutputId, setSelectedOutputId] = useState<string | undefined>();
  const drag = useDragPosition();

  // Avoid surfacing the opaque masked-call reference ("contact:…"); web never places masked calls, but
  // keep the guard so a stray value can't leak into the label.
  const label =
    remoteName ||
    (remoteNumber && !remoteNumber.startsWith('contact:') ? remoteNumber : 'Ongoing call');
  const primary = calls.find((c) => c.isPrimary);
  const primaryHeld = primary?.status === 'held';
  // The job matched to the active call (inbound only — outbound carries no context). The invoice
  // number is the human-facing handle; the id is the fallback and what actually opens the job.
  const job = primary?.incomingContext?.job;
  const jobLabel = job ? `Job ${job.ref ?? job.id.slice(0, 8)}` : null;
  const status =
    primaryHeld ? 'On hold' :
    callState === 'connected' ? formatDuration(callDuration) :
    callState === 'ringing' ? 'Ringing…' :
    callState === 'reconnecting' ? 'Reconnecting…' :
    callState === 'ended' ? 'Call ended' :
    'Connecting…';

  const hasOutputPicker = availableAudioDevices.length > 0;
  // Hold needs an established call to act on; there is nothing to hold while dialing or after the end.
  const canHold = !!primary && (primary.status === 'connected' || primary.status === 'held');

  // Two or more lines get the roomier "call board" layout; a single call keeps the original card.
  const isMulti = calls.length > 1;
  const showHoldBtn = canHold && !isMulti; // multi mode holds/resumes per line instead
  // Stable order (by id) so switching moves the HIGHLIGHT between cards rather than making the
  // cards jump — the eye follows one line lighting up while the other dims.
  const orderedCalls = isMulti ? [...calls].sort((a, b) => a.callSid.localeCompare(b.callSid)) : calls;

  // Per-line clocks: the context only times the primary, so tick locally while several lines are up.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isMulti) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isMulti]);

  // One-shot flash on the line that JUST became active, so a resume/switch is unmistakable.
  const [flashId, setFlashId] = useState<string | null>(null);
  const prevPrimaryId = useRef<string | undefined>(primary?.callSid);
  useEffect(() => {
    const cur = primary?.callSid;
    if (cur && prevPrimaryId.current && cur !== prevPrimaryId.current) {
      setFlashId(cur);
      const t = setTimeout(() => setFlashId(null), 700);
      prevPrimaryId.current = cur;
      return () => clearTimeout(t);
    }
    prevPrimaryId.current = cur;
  }, [primary?.callSid]);

  if (minimized) {
    return createPortal(
      <button
        onClick={() => setMinimized(false)}
        aria-label="Return to call"
        className="fixed bottom-4 right-4 z-[9999] flex items-center gap-3 rounded-full bg-gray-900 px-4 py-2.5 text-white shadow-2xl ring-1 ring-white/10 transition-transform hover:scale-[1.02]"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
        </span>
        <span className="max-w-[10rem] truncate text-sm font-medium">{label}</span>
        <span className="text-xs text-gray-400">{status}</span>
        <ChevronUp className="h-4 w-4 text-gray-400" />
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={drag.ref}
      style={drag.style}
      className={`fixed bottom-4 right-4 z-[9999] flex max-h-[calc(100vh-2rem)] flex-col ${isMulti ? 'w-96' : 'w-80'} select-none overflow-hidden rounded-2xl bg-gray-900 text-white shadow-2xl ring-1 ring-white/10 transition-[width] duration-200`}
    >
      {/* Header — doubles as the drag handle */}
      <div
        {...drag.handleProps}
        className="flex flex-shrink-0 touch-none cursor-move items-center justify-between gap-2 border-b border-white/10 px-4 py-3"
      >
        {isMulti ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-600/30 text-green-300">
              <Phone className="h-4 w-4" />
            </span>
            <p className="text-sm font-semibold leading-tight">{calls.length} calls</p>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-700 text-sm font-semibold">
              {label.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight">{label}</p>
              <p className="text-xs leading-tight text-gray-400">{status}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setMinimized(true)}
          aria-label="Minimize call"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      {/* Scrollable middle: header (drag handle) and the controls row below stay pinned, so a tall
          stack of call cards or an open keypad scrolls here instead of pushing End/minimize off the
          top of the screen. Its bounded height also keeps the widget short enough that the
          call-waiting banner clears it (see IncomingCallBanner). */}
      <div className="max-h-[18rem] flex-1 overflow-y-auto">
      {/* Error banner */}
      {error && (
        <div className="bg-red-500/15 px-4 py-2 text-center text-xs text-red-300">{error}</div>
      )}

      {/* The job this call is attached to. A real link (underline + open icon) — opens the job
          modal via the provider, which lives under the router; the widget itself is portalled. */}
      {job && jobLabel && (
        <div className="border-b border-white/10 px-4 py-2.5">
          <button
            onClick={() => openJob(job.id)}
            aria-label={`Open ${jobLabel}`}
            className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-medium text-blue-200 transition-colors hover:bg-white/20 hover:text-blue-100"
          >
            <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate underline decoration-blue-300/60 underline-offset-2">{jobLabel}</span>
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 opacity-80" />
          </button>
        </div>
      )}

      {/* Call board (two or more lines). Every line is a uniform card: the active one lights green
          and lifts, the rest dim. Cards keep a stable order, so a switch just glides the highlight
          across — plus a one-shot ring flash on the newly-active line (flashId). */}
      {isMulti && (
        <div className="space-y-2 border-b border-white/10 px-3 py-3">
          {orderedCalls.map((c) => {
            const activeLine = c.isPrimary && c.status !== 'held';
            const incoming = c.status === 'incoming';
            return (
              <div
                key={c.callSid}
                className={`relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-300 ${
                  activeLine
                    ? 'scale-[1.02] bg-green-500/15 ring-2 ring-green-400/70'
                    : incoming
                    ? 'bg-amber-500/10 ring-1 ring-amber-400/40'
                    : 'bg-white/5 opacity-80 ring-1 ring-white/10'
                }`}
              >
                {flashId === c.callSid && (
                  <span className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-green-400 animate-ping" />
                )}
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    activeLine ? 'bg-green-600' : incoming ? 'bg-amber-600' : 'bg-gray-700'
                  }`}
                >
                  {callLabel(c).charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold leading-tight">{callLabel(c)}</p>
                  <p className="flex items-center gap-1.5 text-xs leading-tight text-gray-300">
                    {activeLine && (
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                      </span>
                    )}
                    {lineStatus(c, now)}
                  </p>
                </div>
                {/* Resume/Hold IS the switch control; a ringing line is answered from its banner. */}
                {!incoming && (
                  <button
                    onClick={() => { void setHold(c.callSid, c.status !== 'held'); }}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      c.status === 'held'
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {c.status === 'held' ? 'Resume' : 'Hold'}
                  </button>
                )}
                <button
                  onClick={() => { void endCallById(c.callSid); }}
                  aria-label={`End call with ${callLabel(c)}`}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-500/80 transition-colors hover:bg-red-500"
                >
                  <PhoneOff className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* In-call keypad (DTMF) */}
      {showKeypad && callState === 'connected' && (
        <div className="border-b border-white/10 px-4 py-3">
          {dtmfDigits && (
            <div className="mb-2 rounded-lg bg-white/10 px-3 py-2 text-center font-mono text-lg tracking-widest">
              {dtmfDigits}
            </div>
          )}
          <DialPad dtmfMode onDigitPressed={(d) => setDtmfDigits((prev) => prev + d)} />
        </div>
      )}

      {/* Output device picker */}
      {showOutputs && hasOutputPicker && (
        <div className="border-b border-white/10 py-1">
          {availableAudioDevices.map((device) => {
            const active = (device.id ?? device.type) === (selectedOutputId ?? audioRoute);
            return (
              <button
                key={device.id ?? device.type}
                onClick={() => {
                  selectAudioDevice(device.type, device.id);
                  setSelectedOutputId(device.id);
                  setShowOutputs(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-white/10"
              >
                <span className="flex-shrink-0 text-gray-300">{OUTPUT_ICONS[device.type]}</span>
                <span className="flex-1 truncate">{device.name}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0 text-blue-400" />}
              </button>
            );
          })}
        </div>
      )}

      </div>

      {/* Controls act on the ACTIVE line. Gap tightens when the single-call Hold button is present. */}
      <div className={`flex flex-shrink-0 items-center justify-center px-4 py-4 ${showHoldBtn ? 'gap-4' : 'gap-6'}`}>
        {callState === 'ended' && canRedial ? (
          <>
            <ControlButton label="Dismiss" onClick={endCall} className="bg-red-500 hover:bg-red-600">
              <PhoneOff className="h-5 w-5" />
            </ControlButton>
            <ControlButton label="Redial" onClick={redial} className="bg-green-500 hover:bg-green-600">
              <Phone className="h-5 w-5" />
            </ControlButton>
          </>
        ) : (
          <>
            <ControlButton
              label={isMuted ? 'Unmute' : 'Mute'}
              onClick={toggleMute}
              disabled={callState === 'ended'}
              className={isMuted ? TOGGLE_ON : TOGGLE_OFF}
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </ControlButton>

            <ControlButton
              label="Keypad"
              onClick={() => { setShowKeypad((v) => !v); setDtmfDigits(''); }}
              disabled={callState !== 'connected' || primaryHeld}
              className={showKeypad ? TOGGLE_ON : TOGGLE_OFF}
            >
              <Hash className="h-5 w-5" />
            </ControlButton>

            {showHoldBtn && primary && (
              <ControlButton
                label={primaryHeld ? 'Resume' : 'Hold'}
                onClick={() => { void setHold(primary.callSid, !primaryHeld); }}
                className={primaryHeld ? TOGGLE_ON : TOGGLE_OFF}
              >
                {primaryHeld ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              </ControlButton>
            )}

            {hasOutputPicker && (
              <ControlButton
                label="Audio"
                onClick={() => setShowOutputs((v) => !v)}
                className={showOutputs ? TOGGLE_ON : TOGGLE_OFF}
              >
                {OUTPUT_ICONS[audioRoute ?? 'speaker']}
              </ControlButton>
            )}

            <ControlButton
              label="End"
              onClick={endCall}
              disabled={callState === 'ended'}
              className="bg-red-500 hover:bg-red-600"
            >
              <PhoneOff className="h-5 w-5" />
            </ControlButton>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

interface ControlButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

function ControlButton({ label, onClick, disabled, className = '', children }: ControlButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`flex h-12 w-12 flex-col items-center justify-center rounded-full text-white transition-colors disabled:opacity-40 ${className}`}
    >
      {children}
      <span className="mt-0.5 text-[9px] leading-none">{label}</span>
    </button>
  );
}
